import { useState } from 'react';
import {
  Alert, Box, Button, Divider, Grid, Paper, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { api, ApiError } from '../api/client';
import type { ContractsConfig, DividendPayResult, DividendPreview, FreezeStatus, TxRequestResult } from '../api/types';
import { parseBatch } from './Issuer';
import { AddressWithCopy, PageHeader, fmtUnits } from '../components/common';
import { TxProgressDialog, TxResult } from '../components/TxProgressDialog';

/**
 * 발행자(ST) 자산 관리·통제 — 스냅샷·배당·소각·동결·강제이전 (docs/04 §4.7~4.8, SFR-004).
 * addr = 청약 건별 TokenSecurity 컨트랙트 주소.
 */
export function IssuerSecurities() {
  const [addr, setAddr] = useState('');
  const [txOpen, setTxOpen] = useState(false);
  const [txTitle, setTxTitle] = useState('');
  const [result, setResult] = useState<TxResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  function run<T extends unknown[]>(title: string, fn: (...a: T) => Promise<TxRequestResult>) {
    return (...args: T) => {
      setTxTitle(title); setTxOpen(true); setResult(null); setError(null);
      fn(...args)
        .then((r) => setResult({ status: r.status, txHash: r.txHash, revertReason: r.revertReason }))
        .catch((e) => setError(e instanceof ApiError ? e.message : `${title} 실패`));
    };
  }

  const valid = /^0x[0-9a-fA-F]{40}$/.test(addr);

  return (
    <Box>
      <PageHeader crumb="발행자(ST) · 통제" title="자산 관리 · 통제"
        desc="스냅샷·배당·소각·동결·강제이전을 수행합니다. 되돌릴 수 없는 관리 조치는 신중히 실행하세요." />
      <Paper sx={{ p: 3, mb: 3 }}>
        <TextField fullWidth label="토큰증권 컨트랙트 주소" value={addr} onChange={(e) => setAddr(e.target.value)}
          placeholder="0x…" error={!!addr && !valid} helperText={addr && !valid ? '유효한 주소가 아닙니다' : '청약 건별 배포된 TokenSecurity 주소'} />
      </Paper>

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}><SnapshotDividend addr={addr} valid={valid} run={run} /></Grid>
        <Grid item xs={12} md={6}><BurnFreeze addr={addr} valid={valid} run={run} /></Grid>
        <Grid item xs={12} md={6}><ForcedTransfer addr={addr} valid={valid} run={run} /></Grid>
        <Grid item xs={12}><BatchDistribute addr={addr} valid={valid} run={run} /></Grid>
      </Grid>

      <TxProgressDialog open={txOpen} title={txTitle} pending={!result && !error} result={result} error={error} onClose={() => setTxOpen(false)} />
    </Box>
  );
}

type Runner = <T extends unknown[]>(title: string, fn: (...a: T) => Promise<TxRequestResult>) => (...a: T) => void;

/**
 * 스냅샷 · 배당 (SFR-004) — 스냅샷 시점 보유량 비례로 산정(시뮬레이션)한 뒤,
 * 스테이블코인을 일괄 지급하고 배당 사실을 온체인에 기록한다.
 */
function SnapshotDividend({ addr, valid, run }: { addr: string; valid: boolean; run: Runner }) {
  const [stablecoin, setStablecoin] = useState('');
  const [snapshotId, setSnapshotId] = useState('');
  const [total, setTotal] = useState('');
  const [preview, setPreview] = useState<DividendPreview | null>(null);
  const [previewErr, setPreviewErr] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [paying, setPaying] = useState(false);
  const [payResult, setPayResult] = useState<DividendPayResult | null>(null);
  const [payErr, setPayErr] = useState<string | null>(null);

  /** 배당 지급 — 보유자별 개별 전송이라 결과를 건별로 요약해 보여준다 */
  const pay = async () => {
    setPaying(true); setPayErr(null); setPayResult(null);
    try {
      setPayResult(await api.post<DividendPayResult>(`/securities/${addr}/dividends`, { stablecoin, snapshotId, total }));
    } catch (e) {
      setPayErr(e instanceof ApiError ? e.message : '배당 지급 실패');
    } finally {
      setPaying(false);
    }
  };

  // 기본 재원 = 시스템 스테이블코인 주소(미입력 시 자동 채움)
  const fillStablecoin = async () => {
    if (stablecoin) return;
    try {
      const cfg = await api.get<ContractsConfig>('/config/contracts');
      if (cfg.stablecoin) setStablecoin(cfg.stablecoin);
    } catch {
      /* 무시 — 수동 입력 */
    }
  };

  const loadPreview = async () => {
    setLoading(true); setPreviewErr(null); setPreview(null);
    try {
      await fillStablecoin();
      setPreview(
        await api.get<DividendPreview>(
          `/securities/${addr}/dividends/preview?snapshotId=${encodeURIComponent(snapshotId)}&total=${encodeURIComponent(total)}`,
        ),
      );
    } catch (e) {
      setPreviewErr(e instanceof ApiError ? e.message : '배당 산정 실패');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={2}>스냅샷 · 배당</Typography>
      <Stack spacing={2}>
        <Button variant="outlined" disabled={!valid} onClick={run('스냅샷 생성', () => api.post(`/securities/${addr}/snapshot`, {}))}>
          스냅샷 생성 (snapshot)
        </Button>
        <TextField label="스테이블코인 주소(미입력 시 자동)" value={stablecoin} onChange={(e) => setStablecoin(e.target.value)} size="small" />
        <TextField label="스냅샷 ID" value={snapshotId} onChange={(e) => setSnapshotId(e.target.value)} size="small" />
        <TextField label="총 배당액(KRWS)" value={total} onChange={(e) => setTotal(e.target.value)} size="small" type="number" />

        <Button variant="outlined" disabled={!valid || !snapshotId || !total || loading} onClick={() => void loadPreview()}>
          {loading ? '산정 중…' : '배당 산정 (시뮬레이션)'}
        </Button>
        {previewErr && <Alert severity="error">{previewErr}</Alert>}

        {preview && (
          <Box>
            <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap mb={1}>
              <Typography variant="caption" color="text.secondary">대상 {preview.holders.length}명</Typography>
              <Typography variant="caption" color="text.secondary">지급 예정 {fmtUnits(preview.payable)} KRWS</Typography>
              <Typography variant="caption" color="text.secondary">잔여(절사) {fmtUnits(preview.residual)} KRWS</Typography>
            </Stack>
            <Table size="small">
              <TableHead><TableRow>
                <TableCell>지갑</TableCell>
                <TableCell align="right">스냅샷 보유</TableCell>
                <TableCell align="right">배당액(KRWS)</TableCell>
              </TableRow></TableHead>
              <TableBody>
                {preview.holders.map((h) => (
                  <TableRow key={h.wallet}>
                    <TableCell><AddressWithCopy address={h.wallet} /></TableCell>
                    <TableCell align="right">{fmtUnits(h.balance)}</TableCell>
                    <TableCell align="right">{fmtUnits(h.amount)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>
        )}

        <Button variant="contained" disabled={!valid || !stablecoin || !snapshotId || !total || !preview?.holders.length || paying}
          onClick={() => void pay()}>
          {paying ? `지급 중… (${preview?.holders.length ?? 0}건)` : '배당 지급 실행'}
        </Button>
        {payResult && (
          <Alert severity={payResult.payments.some((p) => p.status !== 'COMPLETED') ? 'warning' : 'success'}>
            지급 {payResult.payments.filter((p) => p.status === 'COMPLETED').length}/{payResult.payments.length}건 ·
            총 {fmtUnits(payResult.paid)} KRWS
            {payResult.skipped > 0 && ` · 기지급 ${payResult.skipped}건 건너뜀`}
            {payResult.payments.filter((p) => p.status !== 'COMPLETED').map((p) => (
              <div key={p.wallet} style={{ fontSize: 12, marginTop: 4 }}>
                실패 {p.wallet.slice(0, 10)}… — {p.reason ?? '사유 미상'}
              </div>
            ))}
          </Alert>
        )}
        {payErr && <Alert severity="error">{payErr}</Alert>}
        <Typography variant="caption" color="text.secondary">
          지급 주체는 <b>발행자 본인 지갑</b>입니다(청약 정산 대금으로 지급). 보유자별로 전송하며 가스는 대납되므로
          META 는 필요 없습니다. 재원·적격성은 전송 전에 검사하고, 이미 지급된 건은 자동으로 건너뜁니다.
        </Typography>
      </Stack>
    </Paper>
  );
}

function BurnFreeze({ addr, valid, run }: { addr: string; valid: boolean; run: Runner }) {
  const [burnFrom, setBurnFrom] = useState('');
  const [burnAmt, setBurnAmt] = useState('');
  const [frzWallet, setFrzWallet] = useState('');
  const [frzAmt, setFrzAmt] = useState('');
  const [status, setStatus] = useState<FreezeStatus | null>(null);

  /** 대상 지갑의 전체/부분 동결 현황 조회 */
  const loadStatus = async () => {
    try {
      setStatus(await api.get<FreezeStatus>(`/securities/${addr}/freeze-status/${frzWallet}`));
    } catch {
      setStatus(null);
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={2}>소각 · 동결</Typography>
      <Stack spacing={2}>
        <TextField label="소각 대상 지갑" value={burnFrom} onChange={(e) => setBurnFrom(e.target.value)} size="small" />
        <TextField label="소각 수량" value={burnAmt} onChange={(e) => setBurnAmt(e.target.value)} size="small" type="number" />
        <Button color="error" variant="contained" disabled={!valid || !burnFrom || !burnAmt}
          onClick={() => { if (confirm(`${burnFrom} 토큰 ${burnAmt} 소각 — 되돌릴 수 없습니다. 진행할까요?`)) run('토큰 소각', () => api.post(`/securities/${addr}/burn`, { from: burnFrom, amount: burnAmt }))(); }}>
          소각 (burn)
        </Button>
        <Divider />
        <TextField label="동결/해제 지갑" value={frzWallet} onChange={(e) => setFrzWallet(e.target.value)} size="small"
          helperText={status ? `보유 ${fmtUnits(status.balance)} · 부분동결 ${fmtUnits(status.frozenTokens)} · 이동가능 ${fmtUnits(status.transferable)}${status.frozen ? ' · 전체동결' : ''}` : ' '} />
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="text" disabled={!valid || !frzWallet} onClick={() => void loadStatus()}>동결 현황 조회</Button>
        </Stack>

        {/* 지갑 전체 동결 — 해당 지갑의 모든 이동 차단 */}
        <Typography variant="caption" color="text.secondary">지갑 전체</Typography>
        <Stack direction="row" spacing={1}>
          <Button color="warning" variant="outlined" fullWidth disabled={!valid || !frzWallet}
            onClick={run('지갑 동결', () => api.post(`/securities/${addr}/freeze`, { wallet: frzWallet, frozen: true }))}>동결</Button>
          <Button variant="outlined" fullWidth disabled={!valid || !frzWallet}
            onClick={run('지갑 해제', () => api.post(`/securities/${addr}/freeze`, { wallet: frzWallet, frozen: false }))}>해제</Button>
        </Stack>

        {/* 부분 동결 — 보유 수량 중 일부만 이동 차단 (SFR-004 "지갑 또는 토큰증권의 이동 권한 통제") */}
        <Typography variant="caption" color="text.secondary">수량 지정(부분 동결)</Typography>
        <TextField label="동결/해제 수량" value={frzAmt} onChange={(e) => setFrzAmt(e.target.value)} size="small" type="number" />
        <Stack direction="row" spacing={1}>
          <Button color="warning" variant="outlined" fullWidth disabled={!valid || !frzWallet || !frzAmt}
            onClick={run('부분 동결', () => api.post(`/securities/${addr}/freeze-partial`, { wallet: frzWallet, amount: frzAmt, frozen: true }))}>부분 동결</Button>
          <Button variant="outlined" fullWidth disabled={!valid || !frzWallet || !frzAmt}
            onClick={run('부분 해제', () => api.post(`/securities/${addr}/freeze-partial`, { wallet: frzWallet, amount: frzAmt, frozen: false }))}>부분 해제</Button>
        </Stack>
      </Stack>
    </Paper>
  );
}

/**
 * 일괄 분배 (SFR-004) — 다수 지갑에 운영(agent) 보유분을 한 번에 전송.
 * 청약 발행(batchMint)과 달리 이미 발행된 물량의 재분배에 쓴다.
 */
function BatchDistribute({ addr, valid, run }: { addr: string; valid: boolean; run: Runner }) {
  const [text, setText] = useState('');
  const items = parseBatch(text);
  const total = items.reduce((a, i) => a + Number(i.amount || 0), 0);
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={2}>일괄 분배</Typography>
      <Stack spacing={2}>
        <TextField label="분배 목록 (한 줄에 `주소,수량`)" value={text} onChange={(e) => setText(e.target.value)}
          multiline minRows={4} size="small" placeholder={'0xabc...,100\n0xdef...,250'}
          error={!!text.trim() && items.length === 0}
          helperText={
            text.trim() && items.length === 0
              ? '형식 오류 — `0x주소,수량` 형태로 한 줄에 하나씩 입력하세요'
              : `${items.length}건 · 합계 ${total.toLocaleString()}`
          } />
        <Button variant="contained" disabled={!valid || items.length === 0}
          onClick={() => {
            if (confirm(`${items.length}건 · 합계 ${total.toLocaleString()} 을 분배합니다. 진행할까요?`)) {
              run('토큰증권 일괄 분배', () => api.post(`/securities/${addr}/batch-transfer`, { items }))();
            }
          }}>
          일괄 분배 (batchTransfer)
        </Button>
        <Typography variant="caption" color="text.secondary">
          운영(agent) 지갑 보유분에서 차감되며, 수신자는 모두 isVerified 상태여야 합니다.
        </Typography>
      </Stack>
    </Paper>
  );
}

function ForcedTransfer({ addr, valid, run }: { addr: string; valid: boolean; run: Runner }) {
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [amount, setAmount] = useState('');
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={2}>강제이전 · 회수</Typography>
      <Stack spacing={2}>
        <TextField label="from (회수 대상)" value={from} onChange={(e) => setFrom(e.target.value)} size="small" />
        <TextField label="to (회수 지갑)" value={to} onChange={(e) => setTo(e.target.value)} size="small" />
        <TextField label="수량" value={amount} onChange={(e) => setAmount(e.target.value)} size="small" type="number" />
        <Button color="error" variant="contained" disabled={!valid || !from || !to || !amount}
          onClick={() => { if (confirm(`${from} → ${to} 강제이전 ${amount} — 되돌릴 수 없는 관리 조치입니다. 진행할까요?`)) run('강제이전', () => api.post(`/securities/${addr}/forced-transfer`, { from, to, amount }))(); }}>
          강제이전 (forcedTransfer)
        </Button>
        <Typography variant="caption" color="text.secondary">동결·부분동결을 우회해 토큰을 회수합니다(관리자 권한).</Typography>
      </Stack>
    </Paper>
  );
}
